<div class="w-[600px]">
  <!-- Header -->
  <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
    <div class="flex items-center gap-4">
      <div class="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
        <mat-icon [svgIcon]="'tabler_outline:receipt'" class="text-primary-600 dark:text-primary-400 icon-size-6"></mat-icon>
      </div>
      <div>
        <h2 class="text-xl font-bold text-gray-900 dark:text-white">Szczegóły zamówienia</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400 font-mono">{{ order.orderNumber }}</p>
      </div>
    </div>
    <button mat-icon-button (click)="dialogRef.close()">
      <mat-icon [svgIcon]="'tabler_outline:x'"></mat-icon>
    </button>
  </div>

  <!-- Content -->
  <div class="p-6 max-h-[70vh] overflow-y-auto">
    
    <!-- Warning for old pending orders -->
    <div *ngIf="isOldPending()" 
         class="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
      <div class="flex items-center gap-3">
        <mat-icon [svgIcon]="'tabler_outline:alert-triangle'" class="text-red-600 icon-size-6"></mat-icon>
        <div>
          <p class="font-semibold text-red-700 dark:text-red-400">Zamówienie oczekuje {{ getDaysSinceCreation() }} dni</p>
          <p class="text-sm text-red-600 dark:text-red-400">Rozważ anulowanie tego zamówienia.</p>
        </div>
      </div>
    </div>

    <!-- Status -->
    <div class="flex items-center justify-between mb-6">
      <span class="text-sm text-gray-500 dark:text-gray-400">Status zamówienia</span>
      <span 
        class="px-4 py-2 rounded-lg text-sm font-semibold"
        [ngClass]="getStatusClass(order.status)">
        {{ getStatusLabel(order.status) }}
      </span>
    </div>

    <!-- Plan info -->
    <div class="p-4 rounded-xl mb-6" [ngClass]="{
      'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800': order.planStage === 1,
      'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800': order.planStage === 2,
      'bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700': order.planStage === 0,
      'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800': order.planStage === 3
    }">
      <div class="flex items-center gap-4">
        <span 
          class="px-3 py-1 rounded-full text-xs font-bold"
          [ngClass]="getTypeColor(order.planStage)">
          {{ getTypeLabel(order.planStage) }}
        </span>
        <div>
          <p class="font-bold text-gray-900 dark:text-white">{{ order.planName }}</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">{{ getDurationLabel() }}</p>
        </div>
      </div>
    </div>

    <!-- Order details -->
    <div class="space-y-4 mb-6">
      <h4 class="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Szczegóły płatności</h4>
      
      <div class="grid grid-cols-2 gap-4">
        <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Metoda płatności</p>
          <p class="font-medium text-gray-900 dark:text-white">{{ getPaymentMethodLabel() }}</p>
        </div>
        <div class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Data złożenia</p>
          <p class="font-medium text-gray-900 dark:text-white">{{ formatDate(order.createdAt) }}</p>
        </div>
      </div>

      <!-- Price -->
      <div class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
        <div class="flex justify-between items-center">
          <span class="text-gray-500 dark:text-gray-400">Kwota do zapłaty</span>
          <div class="text-right">
            <span *ngIf="hasDiscount() && order.originalAmount" class="text-sm text-gray-400 line-through mr-2">
              {{ formatPrice(order.originalAmount) }}
            </span>
            <span class="text-2xl font-black text-primary-600">{{ formatPrice(order.amount) }}</span>
          </div>
        </div>
        <div *ngIf="hasDiscount()" class="text-right mt-1">
          <span class="text-sm text-green-600">Rabat: -{{ order.discountPercent }}%</span>
        </div>
      </div>
    </div>

    <mat-divider class="my-6"></mat-divider>

    <!-- Invoice data - Nabywca -->
    <div class="space-y-4">
      <h4 class="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-2">
        <mat-icon [svgIcon]="'tabler_outline:building'" class="icon-size-4"></mat-icon>
        Nabywca
      </h4>
      
      <div class="space-y-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
        <div class="flex justify-between">
          <span class="text-blue-600 dark:text-blue-400 text-sm">Firma</span>
          <span class="font-medium text-gray-900 dark:text-white text-right">{{ order.invoiceData.companyName || '-' }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-blue-600 dark:text-blue-400 text-sm">NIP</span>
          <span class="font-mono font-medium text-gray-900 dark:text-white">{{ order.invoiceData.nip || '-' }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-blue-600 dark:text-blue-400 text-sm">Adres</span>
          <span class="font-medium text-gray-900 dark:text-white text-right">
            {{ order.invoiceData.street }}<br>
            {{ order.invoiceData.postalCode }} {{ order.invoiceData.city }}
          </span>
        </div>
      </div>
    </div>

    <!-- Receiver data - Odbiorca (jeśli jest) -->
    <div *ngIf="order.hasReceiver && order.receiverData" class="space-y-4 mt-6">
      <h4 class="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-2">
        <mat-icon [svgIcon]="'tabler_outline:user'" class="icon-size-4"></mat-icon>
        Odbiorca
      </h4>
      
      <div class="space-y-3 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-xl">
        <div class="flex justify-between">
          <span class="text-indigo-600 dark:text-indigo-400 text-sm">Firma</span>
          <span class="font-medium text-gray-900 dark:text-white text-right">{{ order.receiverData.companyName || '-' }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-indigo-600 dark:text-indigo-400 text-sm">NIP</span>
          <span class="font-mono font-medium text-gray-900 dark:text-white">{{ order.receiverData.nip || '-' }}</span>
        </div>
        <div class="flex justify-between">
          <span class="text-indigo-600 dark:text-indigo-400 text-sm">Adres</span>
          <span class="font-medium text-gray-900 dark:text-white text-right">
            {{ order.receiverData.street }}<br>
            {{ order.receiverData.postalCode }} {{ order.receiverData.city }}
          </span>
        </div>
      </div>
    </div>

    <!-- Invoice section - only for paid orders -->
    <ng-container *ngIf="order.status === 'paid'">
      <mat-divider class="my-6"></mat-divider>

      <div class="space-y-4">
        <h4 class="text-sm font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-2">
          <mat-icon [svgIcon]="'tabler_outline:file-invoice'" class="icon-size-4"></mat-icon>
          Faktura VAT
        </h4>
        
        <!-- Invoice not generated -->
        <div *ngIf="!order.invoiceGenerated && !generatedInvoice" class="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <mat-icon [svgIcon]="'tabler_outline:alert-circle'" class="text-amber-600 icon-size-5"></mat-icon>
              <span class="text-amber-700 dark:text-amber-300">Faktura nie została jeszcze wygenerowana</span>
            </div>
            <button 
              mat-flat-button 
              color="primary"
              (click)="generateInvoice()"
              [disabled]="isGeneratingInvoice"
              class="!rounded-lg">
              <mat-spinner *ngIf="isGeneratingInvoice" diameter="18" class="mr-2"></mat-spinner>
              <mat-icon *ngIf="!isGeneratingInvoice" [svgIcon]="'tabler_outline:file-plus'" class="mr-2"></mat-icon>
              Generuj fakturę
            </button>
          </div>
        </div>

        <!-- Invoice generated -->
        <div *ngIf="order.invoiceGenerated || generatedInvoice" class="space-y-4">
          
          <!-- Ładowanie faktury -->
          <div *ngIf="isLoadingInvoice" class="flex items-center justify-center py-6">
            <mat-spinner diameter="32"></mat-spinner>
          </div>

          <!-- Faktura Card -->
          <div *ngIf="!isLoadingInvoice && generatedInvoice" 
               class="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
            
            <!-- Card Header - PDF -->
            <div 
              (click)="openInvoicePdf()"
              class="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors border-b border-gray-100 dark:border-gray-700">
              <!-- PDF Icon -->
              <div class="w-10 h-10 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                <mat-icon [svgIcon]="'tabler_outline:file-type-pdf'" class="text-red-500 icon-size-6"></mat-icon>
              </div>
              <!-- Info -->
              <div class="flex-1 min-w-0">
                <p class="font-semibold text-gray-900 dark:text-white">{{ generatedInvoice.documentNumber }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">{{ generatedInvoice.issueDate ? formatDate(generatedInvoice.issueDate) : '-' }}</p>
              </div>
              <!-- Open Button -->
              <button mat-icon-button matTooltip="Otwórz PDF">
                <mat-icon [svgIcon]="'tabler_outline:external-link'" class="text-gray-400"></mat-icon>
              </button>
            </div>

            <!-- KSeF Status Section -->
            <div class="p-4 bg-gray-50 dark:bg-gray-800/50">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-3">
                  <!-- KSeF Icon -->
                  <div class="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                       [ngClass]="{
                         'bg-green-100 dark:bg-green-900/30': generatedInvoice.ksefSent,
                         'bg-amber-100 dark:bg-amber-900/30': generatedInvoice.ksefPendingSend && !generatedInvoice.ksefSent,
                         'bg-gray-100 dark:bg-gray-700': !generatedInvoice.ksefSent && !generatedInvoice.ksefPendingSend
                       }">
                    <mat-icon 
                      [svgIcon]="generatedInvoice.ksefSent ? 'tabler_outline:circle-check' : (generatedInvoice.ksefPendingSend ? 'tabler_outline:clock' : 'tabler_outline:send')"
                      class="icon-size-5"
                      [ngClass]="{
                        'text-green-600 dark:text-green-400': generatedInvoice.ksefSent,
                        'text-amber-600 dark:text-amber-400': generatedInvoice.ksefPendingSend && !generatedInvoice.ksefSent,
                        'text-gray-500 dark:text-gray-400': !generatedInvoice.ksefSent && !generatedInvoice.ksefPendingSend
                      }">
                    </mat-icon>
                  </div>
                  
                  <!-- Status text -->
                  <div>
                    <p class="text-sm font-medium"
                       [ngClass]="{
                         'text-green-700 dark:text-green-400': generatedInvoice.ksefSent,
                         'text-amber-700 dark:text-amber-400': generatedInvoice.ksefPendingSend && !generatedInvoice.ksefSent,
                         'text-gray-700 dark:text-gray-300': !generatedInvoice.ksefSent && !generatedInvoice.ksefPendingSend
                       }">
                      <ng-container *ngIf="generatedInvoice.ksefSent">Wysłano do KSeF</ng-container>
                      <ng-container *ngIf="generatedInvoice.ksefPendingSend && !generatedInvoice.ksefSent">Oczekuje na wysyłkę</ng-container>
                      <ng-container *ngIf="!generatedInvoice.ksefSent && !generatedInvoice.ksefPendingSend">Nie wysłano do KSeF</ng-container>
                    </p>
                    <p *ngIf="generatedInvoice.ksefNumber" class="text-xs text-gray-500 dark:text-gray-400 font-mono truncate max-w-[200px]"
                       [matTooltip]="generatedInvoice.ksefNumber">
                      {{ generatedInvoice.ksefNumber }}
                    </p>
                  </div>
                </div>

                <!-- Action buttons -->
                <div class="flex items-center gap-2">
                  <!-- Wyślij do KSeF -->
                  <button 
                    *ngIf="!generatedInvoice.ksefSent && !generatedInvoice.ksefPendingSend"
                    mat-flat-button
                    color="primary"
                    (click)="sendToKsef()"
                    [disabled]="isSendingToKsef"
                    class="!rounded-lg !h-9">
                    <mat-spinner *ngIf="isSendingToKsef" diameter="16" class="mr-1"></mat-spinner>
                    <mat-icon *ngIf="!isSendingToKsef" [svgIcon]="'tabler_outline:send'" class="icon-size-4 mr-1"></mat-icon>
                    <span class="text-sm">Wyślij</span>
                  </button>

                  <!-- Podgląd KSeF -->
                  <button 
                    *ngIf="generatedInvoice.ksefSent && generatedInvoice.ksefNumber"
                    mat-stroked-button 
                    (click)="openKsefDetails()"
                    class="!rounded-lg !h-9"
                    matTooltip="Szczegóły KSeF, XML i UPO">
                    <mat-icon [svgIcon]="'tabler_outline:info-circle'" class="icon-size-4 mr-1"></mat-icon>
                    <span class="text-sm">Szczegóły</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

  </div>

  <!-- Footer with actions -->
  <div class="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
    <div class="flex items-center justify-between">
      <button mat-stroked-button (click)="dialogRef.close()">
        Zamknij
      </button>
      
      <div *ngIf="order.status === 'pending'" class="flex items-center gap-3">
        <button 
          mat-stroked-button 
          color="warn"
          (click)="cancelOrder()"
          [disabled]="isProcessing">
          <mat-icon [svgIcon]="'tabler_outline:x'" class="mr-2"></mat-icon>
          Anuluj zamówienie
        </button>
        <button 
          mat-flat-button 
          color="primary"
          (click)="markAsPaid()"
          [disabled]="isProcessing">
          <mat-spinner *ngIf="isProcessing" diameter="18" class="mr-2"></mat-spinner>
          <mat-icon *ngIf="!isProcessing" [svgIcon]="'tabler_outline:check'" class="mr-2"></mat-icon>
          Oznacz jako opłacone
        </button>
      </div>

      <div *ngIf="order.status !== 'pending'" class="text-sm text-gray-500 dark:text-gray-400">
        <span *ngIf="order.status === 'paid'">Zamówienie zostało opłacone</span>
        <span *ngIf="order.status === 'cancelled'">Zamówienie zostało anulowane</span>
        <span *ngIf="order.status === 'refunded'">Zamówienie zostało zwrócone</span>
      </div>
    </div>
  </div>
</div>
